import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Download, Loader2 } from 'lucide-react';
import { ExportOperationsButton } from './ExportOperationsButton';
import { centsToEuroInput, eurosToCents, fmtEur, PeriodFilter, useOperationsPeriod } from './format';
import {
    downloadOpsPdf,
    uploadOpsPdf,
    useLicenseDisbursements,
    useSetLicenseDisbursement,
    type LicenseDisbursement,
} from './hooks/useOperations';

function fmtDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('es-ES');
}

export function DisbursementsPage() {
    const { from, to, setPeriod } = useOperationsPeriod();
    const { data, isLoading, error, refetch } = useLicenseDisbursements(from, to);
    const save = useSetLicenseDisbursement();
    const [drafts, setDrafts] = useState<Record<string, { paid: string; date: string }>>({});
    const [rowError, setRowError] = useState<string | null>(null);

    useEffect(() => {
        if (!data) return;
        setDrafts((prev) => {
            const next = { ...prev };
            for (const row of data) {
                if (!next[row.orderId]) {
                    next[row.orderId] = {
                        paid: row.officialFeePaidCents == null ? '' : centsToEuroInput(row.officialFeePaidCents),
                        date: row.authorityPaidAt ?? '',
                    };
                }
            }
            return next;
        });
    }, [data]);

    async function saveRow(row: LicenseDisbursement) {
        const draft = drafts[row.orderId];
        const paidCents = draft?.paid.trim() ? eurosToCents(draft.paid) : null;
        if (draft?.paid.trim() && paidCents == null) {
            setRowError('El importe pagado no es válido.');
            return;
        }
        setRowError(null);
        try {
            await save.mutateAsync({
                orderId: row.orderId,
                paidCents,
                paidOn: draft?.date || null,
            });
        } catch (err) {
            setRowError(err instanceof Error ? err.message : 'No se pudo guardar');
        }
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Suplidos"
                description="Tasa de licencia cobrada al cliente y pagada a la administración. Si coinciden, el efecto en el resultado es cero. El justificante no es una factura de gasto."
            />
            <div className="flex flex-wrap items-end gap-3">
                <PeriodFilter from={from} to={to} onChange={setPeriod} />
                <ExportOperationsButton from={from} to={to} />
            </div>
            {error && (
                <p className="text-sm text-destructive">
                    {(error as Error).message}. Hace falta desplegar la API de operaciones.
                </p>
            )}
            {rowError && <p className="text-sm text-destructive">{rowError}</p>}

            <div className="rounded-xl border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[980px]">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Pedido</th>
                            <th className="px-4 py-3">Cobro cliente</th>
                            <th className="px-4 py-3 text-right">Tasa cobrada</th>
                            <th className="px-4 py-3">Pagado a la administración</th>
                            <th className="px-4 py-3">Fecha de pago</th>
                            <th className="px-4 py-3">Cuadre</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {isLoading && (
                            <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                                    Cargando…
                                </td>
                            </tr>
                        )}
                        {!isLoading && (data?.length ?? 0) === 0 && !error && (
                            <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                                    No hay tasas en este periodo.
                                </td>
                            </tr>
                        )}
                        {data?.map((row) => {
                            const draft = drafts[row.orderId];
                            const paidCents = draft?.paid.trim() ? eurosToCents(draft.paid) : row.officialFeePaidCents;
                            const balanced = paidCents != null && paidCents === row.officialFeeCents;
                            return (
                                <tr key={row.orderId} className="hover:bg-muted/20">
                                    <td className="px-4 py-3">
                                        <div className="font-mono text-xs font-semibold">{row.publicId}</div>
                                        {row.customerName && (
                                            <div className="text-xs font-medium">{row.customerName}</div>
                                        )}
                                        {row.customerTaxId && (
                                            <div className="text-xs font-mono text-muted-foreground">{row.customerTaxId}</div>
                                        )}
                                        <div className="text-xs text-muted-foreground">{row.contactEmail}</div>
                                        {row.invoiceNumber && (
                                            <div className="text-xs text-muted-foreground">{row.invoiceNumber}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(row.paidAt)}</td>
                                    <td className="px-4 py-3 text-right">{fmtEur(row.officialFeeCents)}</td>
                                    <td className="px-4 py-3">
                                        <input
                                            value={draft?.paid ?? ''}
                                            onChange={(e) => setDrafts((prev) => ({
                                                ...prev,
                                                [row.orderId]: { paid: e.target.value, date: prev[row.orderId]?.date ?? '' },
                                            }))}
                                            className="w-28 px-2 py-1.5 rounded-lg bg-muted/30 border border-border text-sm"
                                            placeholder="0,00"
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="date"
                                            value={draft?.date ?? ''}
                                            onChange={(e) => setDrafts((prev) => ({
                                                ...prev,
                                                [row.orderId]: { paid: prev[row.orderId]?.paid ?? '', date: e.target.value },
                                            }))}
                                            className="px-2 py-1.5 rounded-lg bg-muted/30 border border-border text-sm"
                                        />
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {balanced ? 'Cuadra' : 'Pendiente'}
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
                                        <button
                                            type="button"
                                            className="text-xs text-primary"
                                            disabled={save.isPending}
                                            onClick={() => void saveRow(row)}
                                        >
                                            Guardar
                                        </button>
                                        <label className="text-xs text-primary cursor-pointer">
                                            Justificante
                                            <input
                                                type="file"
                                                accept="application/pdf"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    void uploadOpsPdf('disbursement', row.orderId, file).then(() => refetch());
                                                }}
                                            />
                                        </label>
                                        {row.hasReceipt && (
                                            <button
                                                type="button"
                                                className="text-xs text-primary"
                                                onClick={() => void downloadOpsPdf('disbursement', row.orderId, `${row.publicId}-tasa.pdf`)}
                                            >
                                                <Download size={12} className="inline" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
